import useMediaQuery from "@/lib/hooks/use-media-query";
import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
} from "react";
import Dialog, {
  DialogAction,
  DialogCancel,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "../dialog/dialog";
import Drawer, {
  DrawerAction,
  DrawerCancel,
  DrawerClose as DrawerCloseComponent,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "../drawer/drawer";

export const AdaptiveDialogContext = createContext(
  {} as {
    open?: boolean;
    isDrawer?: boolean;
    onOpenChange?: (open: boolean) => void;
  },
);

export function useAdaptiveDialog() {
  const context = useContext(AdaptiveDialogContext);
  if (!context) {
    throw new Error("useAdaptiveDialog must be used within an AdaptiveDialog");
  }
  return context;
}

export default function AdaptiveDialog({
  open,
  onOpenChange,
  ...props
}: ComponentProps<typeof Dialog> & ComponentProps<typeof Drawer>) {
  const isDrawer = !useMediaQuery("md");
  const Comp = isDrawer ? Drawer : Dialog;

  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(open ?? false);

  const resolvedOpen = isControlled ? open : internalOpen;
  function handleOpenChange(open: boolean) {
    if (!isControlled) {
      setInternalOpen(open);
    }

    onOpenChange?.(open);
  }

  return (
    <AdaptiveDialogContext.Provider
      value={{ open: resolvedOpen, isDrawer, onOpenChange: handleOpenChange }}
    >
      <Comp open={resolvedOpen} onOpenChange={handleOpenChange} {...props} />
    </AdaptiveDialogContext.Provider>
  );
}

export function AdaptiveDialogTrigger({
  ...props
}: ComponentProps<typeof DialogTrigger> &
  ComponentProps<typeof DrawerTrigger>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerTrigger : DialogTrigger;

  return <Comp {...props} />;
}

export function AdaptiveDialogPortal({
  ...props
}: ComponentProps<typeof DialogPortal> & ComponentProps<typeof DrawerPortal>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerPortal : DialogPortal;

  return <Comp {...props} />;
}

export function AdaptiveDialogClose({
  ...props
}: ComponentProps<typeof DialogClose> &
  ComponentProps<typeof DrawerCloseComponent>) {
  const { isDrawer } = useAdaptiveDialog();

  if (isDrawer) {
    return null; // Drawers don't have a close button
  } else {
    return <DialogClose {...props} />;
  }
}

export function AdaptiveDialogOverlay({
  ...props
}: ComponentProps<typeof DialogOverlay> &
  ComponentProps<typeof DrawerOverlay>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerOverlay : DialogOverlay;

  return <Comp {...props} />;
}

export function AdaptiveDialogContent({
  width,
  ...props
}: ComponentProps<typeof DialogContent> &
  ComponentProps<typeof DrawerContent>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerContent : DialogContent;

  // Only pass width to DialogContent, not DrawerContent
  if (isDrawer) {
    return <Comp {...props} />;
  }

  return <Comp width={width} {...props} />;
}

export function AdaptiveDialogHeader({
  ...props
}: ComponentProps<typeof DialogHeader> & ComponentProps<typeof DrawerHeader>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerHeader : DialogHeader;

  return <Comp {...props} />;
}

export function AdaptiveDialogFooter({
  ...props
}: ComponentProps<typeof DialogFooter> & ComponentProps<typeof DrawerFooter>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerFooter : DialogFooter;

  return <Comp {...props} />;
}

export function AdaptiveDialogTitle({
  ...props
}: ComponentProps<typeof DialogTitle> & ComponentProps<typeof DrawerTitle>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerTitle : DialogTitle;

  return <Comp {...props} />;
}

export function AdaptiveDialogDescription({
  ...props
}: ComponentProps<typeof DialogDescription> &
  ComponentProps<typeof DrawerDescription>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerDescription : DialogDescription;

  return <Comp {...props} />;
}

export function AdaptiveDialogAction({
  ...props
}: ComponentProps<typeof DialogAction> & ComponentProps<typeof DrawerAction>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerAction : DialogAction;

  return <Comp {...props} />;
}

export function AdaptiveDialogCancel({
  ...props
}: ComponentProps<typeof DialogCancel> & ComponentProps<typeof DrawerCancel>) {
  const { isDrawer } = useAdaptiveDialog();
  const Comp = isDrawer ? DrawerCancel : DialogCancel;

  return <Comp {...props} />;
}
